import React, { useState, useEffect, useMemo } from 'react';
import Player from './playerComponent.jsx';
import NewPlayerForm from './newplayer.component.jsx';
import PlayerHWS from './playerHWS.component.jsx';
import PlayerMerge from './playerMerge.component.jsx';
import AgentEditor from './agentEditor.component.jsx';
import { usePlayer } from "../../../../Contexts/playerData.context";
import DragBox2026 from "../../../Universals/DragBox/DragBox2026/dragBox2026.component.jsx";

const BOX_WIDTH = 965;
const STORAGE_KEY_POS = "TOM_BOX_LOCATION";
const DEFAULT_POS = { x: typeof window !== 'undefined' ? window.innerWidth - BOX_WIDTH : 960, y: 95 };

// Simple overlay modal (floats above content, no layout push)
const OverlayModal = ({ title, onClose, children }) => {
  return (
    <>
      {/* Backdrop */}
      <div
        className="position-fixed top-0 start-0 w-100 h-100"
        style={{ background: 'rgba(0,0,0,0.45)', zIndex: 1050 }}
        onClick={onClose}
      />
      {/* Dialog */}
      <div
        className="position-fixed top-50 start-50 translate-middle bg-white shadow rounded p-3"
        style={{ width: 'min(860px, 96vw)', maxHeight: '90vh', overflow: 'auto', zIndex: 1060 }}
        role="dialog"
        aria-modal="true"
        aria-label={title || 'Dialog'}
      >
        <div className="d-flex justify-content-between align-items-center mb-2">
          <div className="fw-bold">{title}</div>
          <button className="btn btn-sm btn-outline-secondary" onClick={onClose} aria-label="Close">
            ✕
          </button>
        </div>
        {children}
      </div>
    </>
  );
};

const DanBox = ({ onGlobalLockChange }) => {
  const [view, setView] = useState('Player');
  const [buttonsLocked, setButtonsLocked] = useState(false);
  const [copied, setCopied] = useState(false);

  // ✅ Pull playerData too
  const { activePlayer, playerData } = usePlayer();

  const checkView = (label) => (view === label ? 'btn-primary' : 'btn-secondary');

  // ✅ Always use the latest player object from playerData (so OneLiner + other views stay updated)
  const currentPlayer = useMemo(() => {
    const id = activePlayer?.FBXID;
    if (!id) return activePlayer || {};

    const arr = Array.isArray(playerData) ? playerData : [];
    const found = arr.find((p) => String(p?.FBXID) === String(id));
    return found || activePlayer || {};
  }, [activePlayer, playerData]);

  // Notify any parent overlay manager when local lock changes
  useEffect(() => {
    if (typeof onGlobalLockChange === 'function') onGlobalLockChange(buttonsLocked);
  }, [buttonsLocked, onGlobalLockChange]);

  // Copy FBXID to clipboard
  const handleCopyFBXID = async () => {
    const id = activePlayer?.FBXID || '';
    if (!id) return;
    try {
      await navigator.clipboard.writeText(String(id));
      setCopied(true);
      setTimeout(() => setCopied(false), 1200);
    } catch (e) {
      console.error('Clipboard copy failed:', e);
    }
  };

  return (
    <DragBox2026
      storageKey={STORAGE_KEY_POS}
      defaultPos={DEFAULT_POS}
      title="Edit/Add - "
      countText={activePlayer ? `${activePlayer.last}, ${activePlayer.first} [${activePlayer.school}]` : 'No Player Selected'}
      minWidth={960}
      maxWidth={960}
      zIndex={500}
      showReset={true}
    >
      <div>
        {/* Buttons row with a cover overlay when buttonsLocked is true */}
        <div className="position-relative mb-1">
          <div className="d-flex justify-content-between gap-2">
            {['Player', 'New Player', 'HWS', 'Merge', 'Agent'].map((label) => (
              <div key={label} className="col-7">
                <button
                  type="button"
                  className={`btn btn-sm w-100 text-white text-uppercase ${checkView(label)}`}
                  onClick={() => !buttonsLocked && setView(label)}
                  disabled={buttonsLocked}
                  aria-disabled={buttonsLocked}
                >
                  {label}
                </button>
              </div>
            ))}

            <div className="col-7">
              <button
                type="button"
                className={`btn btn-sm w-100 text-white ${copied ? 'btn-success' : 'bg-warning'}`}
                onClick={handleCopyFBXID}
                disabled={buttonsLocked || !activePlayer?.FBXID}
                title={activePlayer?.FBXID ? 'Click to copy FBXID' : 'No FBXID available'}
                aria-live="polite"
              >
                {copied ? 'Copied!' : (activePlayer?.FBXID ? String(activePlayer.FBXID) : 'FBXID')}
              </button>
            </div>
          </div>

          {buttonsLocked && (
            <div
              className="position-absolute top-0 start-0 w-100 h-100 d-flex align-items-center justify-content-center"
              style={{
                background: 'rgba(255,255,255,0.6)',
                pointerEvents: 'auto',
                borderRadius: 6,
              }}
            >
              <span className="badge bg-secondary">Editing… Save or Escape</span>
            </div>
          )}
        </div>

        <div id="contentArea">
          {view === 'Player' && (
            <div id="playerBox" style={{ width: '100%' }}>
              <Player currentPlayer={currentPlayer} onModalChange={setButtonsLocked} />
            </div>
          )}

          {view === 'HWS' && <div className="mt-4"><PlayerHWS /></div>}
          {view === 'Merge' && <div className="mt-4"><PlayerMerge /></div>}
          {view === 'Agent' && <div className="mt-4"><AgentEditor /></div>}
        </div>

        {view === 'New Player' && (
          <div id="playerBox" style={{ width: '100%' }}>
            <NewPlayerForm onSaveSuccess={() => setView('Player')} />
          </div>
        )}
      </div>
    </DragBox2026>
  );
};

export default DanBox;
